import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Users, 
  Edit, 
  Search,
  X,
  Save,
  FilterIcon,
  XIcon,
  Download,
  FileText,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Columns,
  Briefcase,
  CalendarRange,
  PieChart,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import FormDrawer from './FormDrawer';
import Avatar from './Avatar';
import ViewTabs from './ui/ViewTabs';
import PeopleView, { PEOPLE_COLUMNS } from './resource/PeopleView';
import ProjectsView from './resource/ProjectsView';
import TimelineView from './resource/TimelineView';
import InsightsView from './resource/InsightsView';
import AllocationDrawer from './resource/AllocationDrawer';
import {
  isResourceBillable,
  RESOURCE_BILLABILITY_OPTIONS,
  RESOURCE_BILLABILITY_VALUES,
} from './resource/utilizationHelpers';
import { ROUTES } from '../constants/routes';

const FILTERS_VISIBILITY_KEY = 'resourceUtilization_showFilters';
// Separate key from the old legacy-table settings, whose column names no longer exist.
const VISIBLE_COLUMNS_KEY = 'resourceUtilization_peopleColumns';
// Shared localStorage key so the "By team lead" expand/collapse pref inside
// the Insights sub-view persists across sessions. InsightsView owns the
// read/write; we only pass the key down.
const TEAM_LEAD_BREAKDOWN_KEY = 'resourceUtilization_showTeamLeadBreakdown';

const leadDisplayName = (lead) => {
  const fn = typeof lead?.full_name === 'string' ? lead.full_name.trim() : '';
  const un = typeof lead?.username === 'string' ? lead.username.trim() : '';
  return fn || un || '';
};

const ResourceUtilization = () => {
  const { user } = useAuth();
  const [resourceData, setResourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeRefreshing, setRangeRefreshing] = useState(false);
  // Session cache keyed by range preset so switching back to a previously
  // visited range is instant. Fresh data is still fetched in the background
  // (stale-while-revalidate), so the user never waits on the network.
  const rangeCacheRef = useRef(new Map());
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(FILTERS_VISIBILITY_KEY);
      if (saved === null) return false;
      return saved === 'true';
    } catch {
      return false;
    }
  });
  // "Show team lead breakdown" toggle moved to InsightsView, which owns its
  // own localStorage-backed expand/collapse state via TEAM_LEAD_BREAKDOWN_KEY.
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    department: '',
    current_team: '',
    future_team: [],
    team_lead: '',
    cohort: '',
    comments: '',
    primary_project_id: '',
    billability: ''
  });
  const [teamLeadDropdownOpen, setTeamLeadDropdownOpen] = useState(false);
  const [teamLeadSearch, setTeamLeadSearch] = useState('');
  const teamLeadDropdownRef = useRef(null);
  const teamLeadSearchRef = useRef(null);
  const [futureProjectsDropdownOpen, setFutureProjectsDropdownOpen] = useState(false);
  const [futureProjectsSearch, setFutureProjectsSearch] = useState('');
  const futureProjectsDropdownRef = useRef(null);
  const futureProjectsSearchRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20, 50];

  const navigate = useNavigate();
  const location = useLocation();

  // Filter state - initialize with all options selected
  const [selectedResourceName, setSelectedResourceName] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCurrentProjects, setSelectedCurrentProjects] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTeamLeads, setSelectedTeamLeads] = useState([]);
  const [selectedHasTasks, setSelectedHasTasks] = useState('all');
  const [selectedComments, setSelectedComments] = useState('');
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [selectedBillable, setSelectedBillable] = useState('all'); // all | billable | non_billable
  const [selectedKind, setSelectedKind] = useState('all'); // all | project | product
  const [selectedRange, setSelectedRange] = useState('this_week'); // this_week | last_week | this_month | last_month | last_3_months | last_6_months
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);

  // Multi-view (People / Projects / Timeline / Insights). The active view is
  // derived from the URL so sidebar sub-links, deep links, and the in-page
  // tab strip stay in sync. Switching tabs calls `navigate(...)` so the URL
  // (and sidebar highlight) tracks the user's current view.
  const VIEW_TO_PATH = React.useMemo(
    () => ({
      people: ROUTES.RESOURCE_UTIL_PEOPLE,
      projects: ROUTES.RESOURCE_UTIL_PROJECTS,
      timeline: ROUTES.RESOURCE_UTIL_TIMELINE,
      insights: ROUTES.RESOURCE_UTIL_INSIGHTS,
    }),
    [],
  );
  const activeView = React.useMemo(() => {
    const p = location.pathname;
    if (p.startsWith(ROUTES.RESOURCE_UTIL_PROJECTS)) return 'projects';
    if (p.startsWith(ROUTES.RESOURCE_UTIL_TIMELINE)) return 'timeline';
    if (p.startsWith(ROUTES.RESOURCE_UTIL_INSIGHTS)) return 'insights';
    return 'people';
  }, [location.pathname]);
  const setActiveView = React.useCallback(
    (viewId) => {
      const target = VIEW_TO_PATH[viewId] || ROUTES.RESOURCE_UTIL_PEOPLE;
      if (location.pathname !== target) navigate(target);
    },
    [VIEW_TO_PATH, location.pathname, navigate],
  );

  // Normalize the bare `/resource-utilization` route to the People default so
  // the sidebar highlight + ViewTabs always have a concrete sub-route to key
  // off (users won't see a flash of empty state while React Router matches).
  React.useEffect(() => {
    if (location.pathname === ROUTES.RESOURCE_UTILIZATION) {
      navigate(ROUTES.RESOURCE_UTIL_PEOPLE, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Allocation drawer
  const [showAllocationDrawer, setShowAllocationDrawer] = useState(false);
  const [allocationDrawerUser, setAllocationDrawerUser] = useState(null);
  const [allocationDrawerProjectId, setAllocationDrawerProjectId] = useState(null);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [selectedUserTasks, setSelectedUserTasks] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [taskModalPage, setTaskModalPage] = useState(1);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskModalPageSize] = useState(5);
  const [showTeamLeadModal, setShowTeamLeadModal] = useState(false);
  const [selectedTeamLeadGroup, setSelectedTeamLeadGroup] = useState(null);
  const [showUtilizationModal, setShowUtilizationModal] = useState(false);
  const [selectedUtilizationUser, setSelectedUtilizationUser] = useState(null);

  // Export state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const exportDropdownRef = useRef();

  // Column visibility for the People table - load from localStorage or default all visible
  const defaultColumns = Object.fromEntries(PEOPLE_COLUMNS.map(c => [c.key, true]));
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(VISIBLE_COLUMNS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultColumns, ...parsed };
      }
    } catch (_) {}
    return defaultColumns;
  });
  const [showColumnsDropdown, setShowColumnsDropdown] = useState(false);
  const columnsDropdownRef = useRef();

  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  };

  // Get all available options for each filter
  const getAllDepartments = () => Array.from(new Set((resourceData?.users || []).map(u => u.department).filter(Boolean)));
  const getAllCurrentProjects = () => Array.from(new Set((resourceData?.users || []).flatMap(u => u.current_projects || []).map(p => p?.name).filter(Boolean)));
  const getAllClients = () => Array.from(new Set((resourceData?.projects || []).map(p => p?.client).filter(Boolean)));
  const getAllTeamLeads = () => Array.from(new Set((resourceData?.users || []).map(u => u.team_lead).filter(Boolean)));
  const getAllCohorts = () => {
    const fromFilterOptions = resourceData?.filter_options?.cohorts || [];
    const fromUsers = (resourceData?.users || []).map(u => u.cohort).filter(Boolean);
    return Array.from(new Set([...(fromFilterOptions || []), ...fromUsers])).sort();
  };

  // Count active filters - only count when NOT all options are selected
  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedResourceName) count++;
    
    // Only count as active if not all departments are selected
    const allDepts = getAllDepartments();
    if (selectedDepartments.length > 0 && selectedDepartments.length < allDepts.length) count++;
    
    // Only count as active if not all projects are selected
    const allProjects = getAllCurrentProjects();
    if (selectedCurrentProjects.length > 0 && selectedCurrentProjects.length < allProjects.length) count++;
    
    // Only count as active if not all clients are selected
    const allClients = getAllClients();
    if (selectedClients.length > 0 && selectedClients.length < allClients.length) count++;
    
    // Only count as active if not all team leads are selected
    const allTeamLeads = getAllTeamLeads();
    if (selectedTeamLeads.length > 0 && selectedTeamLeads.length < allTeamLeads.length) count++;
    
    if (selectedHasTasks !== 'all') count++;
    if (selectedComments) count++;

    // Cohort filter: only count when a subset is selected
    const allCohorts = getAllCohorts();
    if (selectedCohorts.length > 0 && selectedCohorts.length < allCohorts.length) count++;
    if (selectedBillable !== 'all') count++;
    if (selectedKind !== 'all') count++;
    if (selectedRange !== 'this_week') count++;
    if (showConflictsOnly) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Do not pre-load any filters on open - show all employees by default

  // Multi-select helper functions
  const toggleArrayItem = (array, setArray, item) => {
    if (array.includes(item)) {
      setArray(array.filter(i => i !== item));
    } else {
      setArray([...array, item]);
    }
  };

  const clearArrayFilter = (setArray) => {
    setArray([]);
  };

  // Get filtered options based on other selections
  const getFilteredDepartments = () => {
    let filteredUsers = resourceData?.users || [];
    
    // Filter by selected projects
    if (selectedCurrentProjects.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        user.current_projects?.some(p => selectedCurrentProjects.includes(p?.name))
      );
    }
    
    // Filter by selected clients
    if (selectedClients.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        user.current_projects?.some(p => selectedClients.includes(p?.client))
      );
    }
    
    // Filter by selected team leads
    if (selectedTeamLeads.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        selectedTeamLeads.includes(user.team_lead)
      );
    }
    
    return Array.from(new Set(filteredUsers.map(u => u.department).filter(Boolean)));
  };

  const getFilteredCurrentProjects = () => {
    let filteredUsers = resourceData?.users || [];
    
    // Filter by selected departments
    if (selectedDepartments.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        selectedDepartments.includes(user.department)
      );
    }
    
    // Filter by selected clients
    if (selectedClients.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        user.current_projects?.some(p => selectedClients.includes(p?.client))
      );
    }
    
    // Filter by selected team leads
    if (selectedTeamLeads.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        selectedTeamLeads.includes(user.team_lead)
      );
    }
    
    return Array.from(new Set(filteredUsers.flatMap(u => u.current_projects || []).map(p => p?.name).filter(Boolean)));
  };

  const getFilteredClients = () => {
    let filteredUsers = resourceData?.users || [];
    
    // Filter by selected departments
    if (selectedDepartments.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        selectedDepartments.includes(user.department)
      );
    }
    
    // Filter by selected projects
    if (selectedCurrentProjects.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        user.current_projects?.some(p => selectedCurrentProjects.includes(p?.name))
      );
    }
    
    // Filter by selected team leads
    if (selectedTeamLeads.length > 0) {
      filteredUsers = filteredUsers.filter(user => 
        selectedTeamLeads.includes(user.team_lead)
      );
    }
    
    return Array.from(new Set(filteredUsers.flatMap(u => u.current_projects || []).map(p => p?.client).filter(Boolean)));
  };

  const getFilteredTeamLeads = () => {
    // Use same canonical list as Edit modal (admin + project_lead) for consistency
    const teamLeads = resourceData?.team_leads || [];
    return teamLeads
      .map(lead => typeof lead.full_name === 'string' ? lead.full_name : (typeof lead.username === 'string' ? lead.username : null))
      .filter(Boolean);
  };

  // Multi-select dropdown component with search
  const MultiSelectDropdown = ({ label, options, selectedItems, onToggle, onClear, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
          setFilterSearch('');
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isOpen) {
        setFilterSearch('');
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }, [isOpen]);

    const searchLower = (filterSearch || '').toLowerCase();
    const filteredOptions = options.filter(opt =>
      String(opt).toLowerCase().includes(searchLower)
    );

    const allSelected = options.length > 0 && selectedItems.length === options.length;
    const displayText = allSelected ? 'All selected' : 
                       selectedItems.length === 0 ? placeholder : 
                       `${selectedItems.length} selected`;

    return (
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-secondary-700 mb-1">
          {label}
        </label>
        <button
          type="button"
          className="input-field w-full text-left flex items-center justify-between"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={selectedItems.length === 0 ? 'text-gray-400' : ''}>
            {displayText}
          </span>
          <svg className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
            <div className="p-2 border-b border-gray-200 shrink-0">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="input-field w-full pl-8 py-1.5 text-sm"
                />
                {filterSearch && (
                  <button
                    type="button"
                    onClick={() => setFilterSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs font-medium text-gray-600">Select options:</span>
                {selectedItems.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear();
                    }}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0 p-2">
              {filteredOptions.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">No options match &quot;{filterSearch}&quot;</p>
              ) : (
                filteredOptions.map((option) => (
                  <label key={option} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(option)}
                      onChange={() => onToggle(option)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Initial fetch shows the full-page spinner; subsequent fetches triggered
  // by changing the date range are silent so the view doesn't flash.
  const initialFetchDoneRef = useRef(false);
  useEffect(() => {
    const isInitial = !initialFetchDoneRef.current;
    initialFetchDoneRef.current = true;
    fetchResourceData(selectedRange, { silent: !isInitial });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRange]);

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_VISIBILITY_KEY, String(showFilters));
    } catch {
      // ignore storage errors
    }
  }, [showFilters]);

  // Team-lead breakdown persistence now lives inside InsightsView.

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedResourceName, selectedDepartments, selectedCurrentProjects, selectedClients, selectedTeamLeads, selectedHasTasks, selectedComments, selectedBillable, selectedKind]);

  // Close export and columns dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (columnsDropdownRef.current && !columnsDropdownRef.current.contains(event.target)) {
        setShowColumnsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Export helpers --------------------------------------------------------
  // Resource utilization carries two kinds of "project" rows:
  //   - kind === 'product'  (internal product / platform work)
  //   - kind === 'project'  (external / client project — default)
  // The export now surfaces that split both on the summary row (kind-split
  // name & hour columns) and as a dedicated detail table (one row per
  // employee × project/product) so analysts don't have to recompute it.
  const normalizeKind = (value) => (value === 'product' ? 'product' : 'project');

  // Build a quick project_id -> kind map from any kind-bearing collection on
  // the user payload. Allocations don't carry `kind`, so we backfill from
  // current/future/per_project entries.
  const buildKindLookup = (user) => {
    const map = new Map();
    const add = (id, kind) => {
      if (id == null) return;
      const key = String(id);
      if (!map.has(key)) map.set(key, normalizeKind(kind));
    };
    (user.current_projects || []).forEach((p) => add(p?.id, p?.kind));
    (user.future_projects || []).forEach((p) => add(p?.id, p?.kind));
    (user.per_project || []).forEach((p) => add(p?.project_id, p?.kind));
    return map;
  };

  const splitProjectsByKind = (projects, kindLookup) => {
    const out = { project: [], product: [] };
    (projects || []).forEach((p) => {
      const resolved = normalizeKind(p?.kind || kindLookup.get(String(p?.id)));
      out[resolved].push(p);
    });
    return out;
  };

  const buildSummaryRow = (user) => {
    const taskLinks =
      user.tasks
        ?.map(
          (task) =>
            `${task.name || 'Untitled Task'} (${window.location.origin}/tasks/${task.id})`,
        )
        .join('; ') || '';
    const primaryProject =
      user.primary_project_id &&
      user.current_projects?.find(
        (p) => String(p.id) === String(user.primary_project_id),
      );

    const kindLookup = buildKindLookup(user);
    const currentSplit = splitProjectsByKind(user.current_projects, kindLookup);
    const futureSplit = splitProjectsByKind(user.future_projects, kindLookup);

    // Hour roll-ups per kind come from `per_project` where tracking actually
    // lives. Backend serialises BOTH:
    //   - `tracked_hours` → lifetime total
    //   - `week_hours`    → scoped to the selected date range
    // We expose both as separate columns so analysts can tell them apart.
    const perKindTotals = { project: {}, product: {} };
    (user.per_project || []).forEach((p) => {
      const kind = normalizeKind(p.kind || kindLookup.get(String(p.project_id)));
      const bucket = (perKindTotals[kind] = perKindTotals[kind] || {});
      bucket.tracked = (bucket.tracked || 0) + Number(p.tracked_hours || 0);
      bucket.week = (bucket.week || 0) + Number(p.week_hours || 0);
      bucket.billable_tracked =
        (bucket.billable_tracked || 0) +
        (p.is_billable ? Number(p.tracked_hours || 0) : 0);
      bucket.billable_week =
        (bucket.billable_week || 0) +
        (p.is_billable ? Number(p.week_hours || 0) : 0);
      bucket.task_count = (bucket.task_count || 0) + Number(p.task_count || 0);
    });

    const round1 = (n) => (n == null ? '' : Math.round(Number(n) * 10) / 10);

    const allocationsSummary = (user.allocations || [])
      .map((a) => {
        const kind = normalizeKind(kindLookup.get(String(a.project_id)));
        return `[${kind}] ${a.project_name || 'Project'}${a.phase ? ' / ' + a.phase : ''}: ${
          a.start_date ? String(a.start_date).slice(0, 10) : '?'
        } -> ${a.end_date ? String(a.end_date).slice(0, 10) : '?'} @ ${a.allocation_percent ?? 0}%`;
      })
      .join('; ');

    const primaryKind = primaryProject
      ? normalizeKind(
          primaryProject.kind || kindLookup.get(String(primaryProject.id)),
        )
      : '';

    return {
      'Resource Name': user.full_name || user.username || 'Unknown',
      Department: user.department || '',
      Cohort: user.cohort || '',
      'Team Lead': user.team_lead || '',
      // Kind-split project/product columns (new) — give analysts the
      // project-vs-product breakdown without parsing text.
      'Projects (Count)': currentSplit.project.length,
      Projects:
        currentSplit.project.map((p) => p.name).filter(Boolean).join(', ') || '',
      'Products (Count)': currentSplit.product.length,
      Products:
        currentSplit.product.map((p) => p.name).filter(Boolean).join(', ') || '',
      'Primary Project': primaryProject?.name || '',
      'Primary Kind': primaryKind,
      Client:
        user.current_projects
          ?.map((p) => p.client)
          .filter(Boolean)
          .join(', ') || '',
      'Future Projects (Count)': futureSplit.project.length,
      'Future Projects':
        futureSplit.project.map((p) => p.name).filter(Boolean).join(', ') || '',
      'Future Products (Count)': futureSplit.product.length,
      'Future Products':
        futureSplit.product.map((p) => p.name).filter(Boolean).join(', ') || '',
      'Task Links': taskLinks,
      // ---- Range-scoped (filter aware) --------------------------------
      'Utilization %': user.utilization?.percent ?? '',
      'Range Hours': user.utilization?.week_hours ?? '',
      'Range Target Hours': user.utilization?.target_hours ?? '',
      'Utilization Status': user.utilization?.status || '',
      'Project Hours (Range)': round1(perKindTotals.project.week),
      'Product Hours (Range)': round1(perKindTotals.product.week),
      'Billable Hours (Range)': user.billable_week_hours ?? '',
      'Non-billable Hours (Range)': user.non_billable_week_hours ?? '',
      'Billable Project Hours (Range)': round1(
        perKindTotals.project.billable_week,
      ),
      'Billable Product Hours (Range)': round1(
        perKindTotals.product.billable_week,
      ),
      // ---- Lifetime (all-time totals) ---------------------------------
      'Project Hours (Lifetime)': round1(perKindTotals.project.tracked),
      'Product Hours (Lifetime)': round1(perKindTotals.product.tracked),
      'Billable Hours (Lifetime)': user.billable_hours ?? '',
      'Non-billable Hours (Lifetime)': user.non_billable_hours ?? '',
      'Billable Project Hours (Lifetime)': round1(
        perKindTotals.project.billable_tracked,
      ),
      'Planned Capacity %': user.planned_capacity ?? '',
      'Free Date': user.free_date ? String(user.free_date).slice(0, 10) : '',
      Shared: user.is_shared ? 'Yes' : 'No',
      'Over-allocated': user.is_over_allocated ? 'Yes' : 'No',
      Allocations: allocationsSummary,
      Comments: user.comments || '',
    };
  };

  // One row per employee × project/product using `per_project`. This is what
  // actually gives the user the project-vs-product segregation they asked for
  // (visible via the `Kind` column), including allocation lifecycle fields.
  const buildProjectDetailRows = (user) => {
    const kindLookup = buildKindLookup(user);
    const rows = (user.per_project || []).map((p) => {
      const kind = normalizeKind(p.kind || kindLookup.get(String(p.project_id)));
      return {
        'Resource Name': user.full_name || user.username || 'Unknown',
        Department: user.department || '',
        Cohort: user.cohort || '',
        'Team Lead': user.team_lead || '',
        Kind: kind === 'product' ? 'Product' : 'Project',
        'Project / Product': p.project_name || '',
        Client: p.client || '',
        Billable: p.is_billable ? 'Yes' : 'No',
        // Explicit (Range) / (Lifetime) suffixes make it obvious which
        // column respects the active date filter.
        'Hours (Range)': p.week_hours ?? 0,
        'Hours (Lifetime)': p.tracked_hours ?? 0,
        'Task Count': p.task_count ?? 0,
        'Active Tasks': p.active_task_count ?? 0,
        'Allocation %': p.allocation_percent ?? '',
        Phase: p.phase || '',
        'Start Date': p.start_date ? String(p.start_date).slice(0, 10) : '',
        'End Date': p.end_date ? String(p.end_date).slice(0, 10) : '',
        Lifecycle: p.lifecycle || '',
      };
    });
    // Include any allocations whose project isn't in per_project (planned but
    // not yet tracked) so the detail sheet is complete.
    const seen = new Set((user.per_project || []).map((p) => String(p.project_id)));
    (user.allocations || []).forEach((a) => {
      if (!a.project_id || seen.has(String(a.project_id))) return;
      const kind = normalizeKind(kindLookup.get(String(a.project_id)));
      rows.push({
        'Resource Name': user.full_name || user.username || 'Unknown',
        Department: user.department || '',
        Cohort: user.cohort || '',
        'Team Lead': user.team_lead || '',
        Kind: kind === 'product' ? 'Product' : 'Project',
        'Project / Product': a.project_name || '',
        Client: a.client || '',
        Billable: a.is_billable ? 'Yes' : 'No',
        'Hours (Range)': 0,
        'Hours (Lifetime)': 0,
        'Task Count': 0,
        'Active Tasks': 0,
        'Allocation %': a.allocation_percent ?? '',
        Phase: a.phase || '',
        'Start Date': a.start_date ? String(a.start_date).slice(0, 10) : '',
        'End Date': a.end_date ? String(a.end_date).slice(0, 10) : '',
        Lifecycle: a.lifecycle || '',
      });
    });
    return rows;
  };

  const prepareExportData = () => {
    const summaryRows = [];
    const projectDetailRows = [];
    filteredUsers.forEach((user) => {
      summaryRows.push(buildSummaryRow(user));
      projectDetailRows.push(...buildProjectDetailRows(user));
    });
    return { summaryRows, projectDetailRows };
  };

  // Serialise an array-of-objects into a CSV string with proper RFC-4180
  // quoting; used for both the summary and the detail downloads.
  const rowsToCsv = (rows) => {
    if (!rows.length) return '';
    const headers = Object.keys(rows[0]);
    const esc = (value) => {
      const str = value == null ? '' : String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    return [
      headers.join(','),
      ...rows.map((row) => headers.map((h) => esc(row[h])).join(',')),
    ].join('\n');
  };

  const downloadBlob = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    setExportLoading(true);
    try {
      const { summaryRows, projectDetailRows } = prepareExportData();
      const stamp = new Date().toISOString().split('T')[0];

      const summaryCsv = rowsToCsv(summaryRows);
      downloadBlob(
        summaryCsv,
        `resource-utilization-${stamp}.csv`,
        'text/csv;charset=utf-8;',
      );

      // Second file carries the per employee × project/product breakdown so
      // the project-vs-product data lives in its own analysable table.
      if (projectDetailRows.length) {
        const detailCsv = rowsToCsv(projectDetailRows);
        downloadBlob(
          detailCsv,
          `resource-utilization-detail-${stamp}.csv`,
          'text/csv;charset=utf-8;',
        );
        toast.success(
          `Exported ${summaryRows.length} resources + ${projectDetailRows.length} project/product rows`,
        );
      } else {
        toast.success(`Exported ${summaryRows.length} resources`);
      }
    } catch (error) {
      console.error('CSV export failed:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExportLoading(false);
      setShowExportDropdown(false);
    }
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Import XLSX dynamically to avoid loading it unless needed
      const XLSX = await import('xlsx');

      const { summaryRows, projectDetailRows } = prepareExportData();

      // ---- Sheet 1: Summary (one row per employee) -----------------------
      const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
      if (summaryRows.length) {
        const range = XLSX.utils.decode_range(summarySheet['!ref']);
        const taskLinksColIndex = Object.keys(summaryRows[0]).indexOf('Task Links');

        if (taskLinksColIndex !== -1) {
          for (let R = range.s.r + 1; R <= range.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({
              c: taskLinksColIndex,
              r: R,
            });
            const cell = summarySheet[cellAddress];
            if (cell && cell.v) {
              const user = filteredUsers[R - 1];
              if (user && user.tasks && user.tasks.length > 0) {
                const taskHyperlinks = user.tasks
                  .map(
                    (task) =>
                      `HYPERLINK("${window.location.origin}/tasks/${task.id}","${task.name || 'Untitled Task'}")`,
                  )
                  .join('; ');
                cell.f = taskHyperlinks;
                delete cell.v;
              }
            }
          }
        }

        // Auto-size columns on the summary sheet.
        const colWidths = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxWidth = 0;
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
            const cell = summarySheet[cellAddress];
            if (cell && (cell.v || cell.f)) {
              const cellValue = cell.v || cell.f || '';
              maxWidth = Math.max(maxWidth, cellValue.toString().length);
            }
          }
          colWidths[C] = { width: Math.min(maxWidth + 2, 50) };
        }
        summarySheet['!cols'] = colWidths;
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // ---- Sheet 2: Projects & Products breakdown ------------------------
      if (projectDetailRows.length) {
        const detailSheet = XLSX.utils.json_to_sheet(projectDetailRows);
        const detailRange = XLSX.utils.decode_range(detailSheet['!ref']);
        const detailWidths = [];
        for (let C = detailRange.s.c; C <= detailRange.e.c; ++C) {
          let maxWidth = 0;
          for (let R = detailRange.s.r; R <= detailRange.e.r; ++R) {
            const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
            const cell = detailSheet[cellAddress];
            if (cell && (cell.v != null || cell.f)) {
              const v = cell.v != null ? cell.v : cell.f || '';
              maxWidth = Math.max(maxWidth, v.toString().length);
            }
          }
          detailWidths[C] = { width: Math.min(maxWidth + 2, 50) };
        }
        detailSheet['!cols'] = detailWidths;
        XLSX.utils.book_append_sheet(
          workbook,
          detailSheet,
          'Projects & Products',
        );
      }

      XLSX.writeFile(
        workbook,
        `resource-utilization-${new Date().toISOString().split('T')[0]}.xlsx`,
      );

      toast.success(
        projectDetailRows.length
          ? `Excel exported with ${summaryRows.length} resources and ${projectDetailRows.length} project/product rows`
          : `Excel exported with ${summaryRows.length} resources`,
      );
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error('Failed to export Excel. Please try CSV instead.');
    } finally {
      setExportLoading(false);
      setShowExportDropdown(false);
    }
  };

  const fetchResourceData = async (
    rangePreset = selectedRange,
    { silent = false, bypassCache = false } = {},
  ) => {
    const preset = rangePreset || 'this_week';
    const cache = rangeCacheRef.current;
    const cached = cache.get(preset);

    // Stale-while-revalidate: paint the cached payload immediately for a
    // snappy filter switch, then refresh in the background. The small
    // spinner next to the Date-range select is the only loading hint.
    if (cached && !bypassCache) {
      setResourceData(cached);
      setLoading(false);
    }

    try {
      if (cached && !bypassCache) {
        setRangeRefreshing(true);
      } else if (silent) {
        setRangeRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await axios.get('/api/resource-utilization/', {
        params: { range: preset },
      });
      cache.set(preset, response.data);
      setResourceData(response.data);
    } catch (error) {
      console.error('Failed to fetch resource data:', error);
      toast.error('Failed to fetch resource utilization data');
      if (!silent && !cached) setResourceData(null);
    } finally {
      setRangeRefreshing(false);
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    if (!user || typeof user !== 'object' || user.id === undefined) {
      toast.error('Invalid user data');
      return;
    }
    setEditingUser(user);
    const futureTeamData = (() => {
      if (Array.isArray(user.future_team)) {
        return user.future_team
          .filter(ft => ft !== null && ft !== undefined)
          .map(ft => {
            if (typeof ft === 'object' && ft !== null) {
              // Handle objects that might be validation errors or project objects
              if (ft.id !== undefined) {
                return String(ft.id);
              } else if (ft.type && ft.loc && ft.msg) {
                // Skip validation error objects
                return null;
              } else {
                // For other objects, try to convert to string safely
                return String(ft);
              }
            } else {
              return String(ft);
            }
          })
          .filter(id => id !== null && id !== '');
      } else if (typeof user.future_team === 'string' && user.future_team.trim() !== '') {
        // Parse comma-separated string back to array
        return user.future_team.split(',').map(id => id.trim()).filter(id => id !== '');
      } else if (user.future_team && typeof user.future_team === 'object' && user.future_team.id !== undefined) {
        return [String(user.future_team.id)];
      } else if (user.future_team) {
        return [String(user.future_team)];
      } else {
        return [];
      }
    })();
    

    
    const newFormData = {
      department: typeof user.department === 'string' ? user.department : '',
      current_team: typeof user.current_team === 'string' ? user.current_team : '',
      future_team: futureTeamData,
      team_lead: typeof user.team_lead === 'string' ? user.team_lead : '',
      cohort: typeof user.cohort === 'string' ? user.cohort : '',
      comments: typeof user.comments === 'string' ? user.comments : '',
      primary_project_id: user.primary_project_id ? String(user.primary_project_id) : '',
      billability: RESOURCE_BILLABILITY_VALUES.includes(user.billability) ? user.billability : ''
    };
    

    setFormData(newFormData);
    setShowEditModal(true);
  };

  const handleShowTasks = async (user) => {
    try {
      setTasksLoading(true);
      
      // Use the existing task data from resourceData instead of making a separate API call
      const userTasks = user.tasks || [];
      setSelectedUserTasks(userTasks);
      setSelectedUserName(user.full_name || user.username || 'Unknown User');
      setTaskModalPage(1); // Reset to first page
      setShowTasksModal(true);
    } catch (error) {
      console.error('Failed to fetch user tasks:', error);
      toast.error('Failed to fetch user tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleCloseTasksModal = () => {
    setShowTasksModal(false);
    setTaskModalPage(1); // Reset pagination
    setSelectedUserTasks([]);
    setSelectedUserName('');
  };

  const handleSave = async () => {
    if (!editingUser || editingUser.id === undefined) {
      toast.error('No user selected for editing');
      return;
    }
    setSaving(true);
    try {

      const futureTeamArray = (formData.future_team || [])
        .filter(id => id !== null && id !== undefined && id !== '')
        .map(id => {
          // Since we're dealing with UUIDs, we should keep them as strings
          // Only convert to number if it's actually a numeric ID
          if (typeof id === 'string' && /^\d+$/.test(id)) {
            const numId = Number(id);
            return isNaN(numId) ? null : numId;
          } else {
            // Keep UUIDs as strings
            return String(id);
          }
        })
        .filter(id => id !== null);
      
      const payload = {
        ...formData,
        future_team: futureTeamArray.length > 0 ? futureTeamArray.join(',') : null,
        primary_project_id: formData.primary_project_id || null,
        billability: formData.billability || null
      };
      const billabilityChanged =
        (formData.billability || null) !== (editingUser.billability || null);

      await axios.patch(`/api/users/${editingUser.id}`, payload);
      toast.success('Resource updated successfully');
      if (billabilityChanged) {
        // Billable hours / insights are recomputed server-side per project.
        fetchResourceData(selectedRange, { silent: true, bypassCache: true });
      }
      
      // Update the specific user in the existing data instead of reloading everything
      setResourceData(prevData => {
        if (!prevData || !prevData.users) return prevData;
        
        return {
          ...prevData,
          users: prevData.users.map(user => 
            user.id === editingUser.id 
              ? { 
                  ...user, 
                  department: formData.department,
                  team_lead: formData.team_lead,
                  cohort: formData.cohort,
                  comments: formData.comments,
                  future_team: formData.future_team,
                  primary_project_id: formData.primary_project_id || null,
                  billability: formData.billability || null
                }
              : user
          )
        };
      });
      setEditingUser(null);
      setShowEditModal(false);
      setTeamLeadDropdownOpen(false);
      setTeamLeadSearch('');
      setFutureProjectsDropdownOpen(false);
      setFutureProjectsSearch('');
      setFormData({
        department: '',
        current_team: '',
        future_team: [],
        team_lead: '',
        cohort: '',
        comments: '',
        primary_project_id: '',
        billability: ''
      });
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update resource');
    } finally {
      setSaving(false);
    }
  };

  // Filter users based on search and column filters
  const filteredUsers = resourceData?.users?.filter(user => {
    // Apply individual column filters
    if (selectedResourceName && !user.full_name?.toLowerCase().includes(selectedResourceName.toLowerCase())) {
      return false;
    }
    
    if (selectedDepartments.length > 0 && !selectedDepartments.includes(user.department)) {
      return false;
    }
    
    if (selectedCurrentProjects.length > 0) {
      const currentProjects = user.current_projects?.map(p => p?.name).filter(Boolean) || [];
      const hasMatchingProject = selectedCurrentProjects.some(project => currentProjects.includes(project));
      if (!hasMatchingProject) {
        return false;
      }
    }
    
    if (selectedClients.length > 0) {
      const currentProjects = user.current_projects || [];
      const userClients = currentProjects.map(p => p.client).filter(Boolean);
      const hasMatchingClient = selectedClients.some(client => userClients.includes(client));
      if (!hasMatchingClient) {
        return false;
      }
    }
    
    if (selectedTeamLeads.length > 0 && !selectedTeamLeads.includes(user.team_lead)) {
      return false;
    }
    
    if (selectedHasTasks !== 'all') {
      const userTaskCount = user.task_count || 0;
      if (selectedHasTasks === 'yes' && userTaskCount === 0) {
        return false;
      }
      if (selectedHasTasks === 'no' && userTaskCount > 0) {
        return false;
      }
    }
    
    if (selectedComments && !user.comments?.toLowerCase().includes(selectedComments.toLowerCase())) {
      return false;
    }

    // Cohort filter (multi-select; only apply when a strict subset is picked)
    if (selectedCohorts.length > 0 && selectedCohorts.length < getAllCohorts().length) {
      if (!user.cohort || !selectedCohorts.includes(user.cohort)) {
        return false;
      }
    }

    // Billability filter. Must match the PeopleView chip logic so the filter
    // and the badge agree: a manual Edit Resource override wins; otherwise
    // "Billable" = any billable hours in the selected range.
    const hasBillableHours = isResourceBillable(user);
    if (selectedBillable === 'billable' && !hasBillableHours) {
      return false;
    }
    if (selectedBillable === 'non_billable' && hasBillableHours) {
      return false;
    }

    // Project vs. product filter — include the user when at least one of
    // their tracked / allocated project entries matches the selected kind.
    if (selectedKind !== 'all') {
      const matches = (user.per_project || []).some(
        (e) => (e?.kind || 'project') === selectedKind,
      );
      if (!matches) return false;
    }

    // Conflicts/shared only
    if (showConflictsOnly && !(user.is_shared || user.is_over_allocated)) {
      return false;
    }

    // Then apply search term filtering across all columns
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Get current projects for this user
    const currentProjects = user.current_projects?.map(p => p?.name).filter(Boolean) || [];
    // Get future projects for this user
    const futureProjects = user.future_projects?.map(p => p?.name).filter(Boolean) || [];
    const userTaskCount = user.task_count || 0;
    
    // Search in all relevant fields
    const matchesSearch = (
      // Basic user info
      (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.username && user.username.toLowerCase().includes(searchLower)) ||
      
      // Role and department
      (user.role && user.role.toLowerCase().includes(searchLower)) ||
      (user.department && user.department.toLowerCase().includes(searchLower)) ||
      (user.team_lead && user.team_lead.toLowerCase().includes(searchLower)) ||
      
      // Current and future projects
      currentProjects.some(projectName => projectName && projectName.toLowerCase().includes(searchLower)) ||
      futureProjects.some(projectName => projectName && projectName.toLowerCase().includes(searchLower)) ||
      
      // Task count
      (String(userTaskCount).includes(searchLower)) ||
      
      // Additional fields
      (user.comments && user.comments.toLowerCase().includes(searchLower)) ||
      (user.current_team && user.current_team.toLowerCase().includes(searchLower)) ||
      (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
      (user.location && user.location.toLowerCase().includes(searchLower)) ||
      (user.skills && Array.isArray(user.skills) && user.skills.some(skill => skill.toLowerCase().includes(searchLower))) ||
      (user.tags && Array.isArray(user.tags) && user.tags.some(tag => tag.toLowerCase().includes(searchLower)))
    );
    
    return matchesSearch;
  }) || [];

  const normalizeTeamLeadKey = (teamLead) => {
    const raw = typeof teamLead === 'string' ? teamLead.trim() : '';
    if (!raw || raw === '-') return 'Unassigned';
    return raw;
  };

  // Team lead bifurcation from currently filtered table rows
  const teamLeadBreakdown = (() => {
    const groups = new Map();
    filteredUsers.forEach((user) => {
      const lead = normalizeTeamLeadKey(user.team_lead);
      if (!groups.has(lead)) groups.set(lead, []);
      groups.get(lead).push(user);
    });

    return Array.from(groups.entries())
      .map(([lead, users]) => ({
        lead,
        count: users.length,
        users: [...users].sort((a, b) =>
          (a.full_name || a.username || '').localeCompare(b.full_name || b.username || '', undefined, {
            sensitivity: 'base',
          })
        ),
      }))
      .sort((a, b) => {
        if (a.lead === 'Unassigned') return 1;
        if (b.lead === 'Unassigned') return -1;
        return b.count - a.count || a.lead.localeCompare(b.lead, undefined, { sensitivity: 'base' });
      });
  })();

  const openTeamLeadModal = (group) => {
    setSelectedTeamLeadGroup(group);
    setShowTeamLeadModal(true);
  };

  const closeTeamLeadModal = () => {
    setShowTeamLeadModal(false);
    setSelectedTeamLeadGroup(null);
  };

  const utilizationStatusLabel = (status) => {
    if (status === 'on_track') return 'On track';
    if (status === 'under_target') return 'Under target';
    if (status === 'critical') return 'Critical';
    return 'Unknown';
  };

  const utilizationStatusClasses = (status) => {
    if (status === 'on_track') {
      return 'text-emerald-600 dark:text-emerald-400';
    }
    if (status === 'under_target') {
      return 'text-amber-600 dark:text-amber-400';
    }
    if (status === 'critical') {
      return 'text-red-600 dark:text-red-400';
    }
    return 'text-primary-700 dark:text-primary-400';
  };

  const utilizationPillClasses = (status) => {
    if (status === 'on_track') {
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    }
    if (status === 'under_target') {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    }
    if (status === 'critical') {
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    }
    return 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200';
  };

  const openUtilizationModal = (user) => {
    setSelectedUtilizationUser(user);
    setShowUtilizationModal(true);
  };

  const closeUtilizationModal = () => {
    setShowUtilizationModal(false);
    setSelectedUtilizationUser(null);
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Task modal pagination calculations
  const taskModalTotalPages = Math.ceil(selectedUserTasks.length / taskModalPageSize);
  const paginatedTasks = selectedUserTasks.slice(
    (taskModalPage - 1) * taskModalPageSize, 
    taskModalPage * taskModalPageSize
  );

  // Unique filter values
  const departments = resourceData?.filter_options?.departments || [];
  const teams = Array.from(new Set((resourceData?.users || []).map(u => u.current_team).filter(val => typeof val === 'string' && val.trim() !== '')));
  const leads = resourceData?.filter_options?.team_leads || [];

  // Team leads for dropdown (API: active admins + project leads with a name)
  const adminUsers = useMemo(
    () => (resourceData?.team_leads || []).filter((l) => Boolean(leadDisplayName(l))),
    [resourceData?.team_leads]
  );

  const editModalDepartmentOptions = useMemo(() => {
    const base = [...(resourceData?.filter_options?.departments || [])];
    const cur = typeof formData.department === 'string' ? formData.department.trim() : '';
    if (cur && !base.includes(cur)) base.push(cur);
    return [...new Set(base)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [resourceData?.filter_options?.departments, formData.department]);

  const editModalTeamLeadOptions = useMemo(() => {
    const leads = adminUsers;
    const cur = typeof formData.team_lead === 'string' ? formData.team_lead.trim() : '';
    if (!cur) return leads;
    const inList = leads.some((l) => {
      const name = leadDisplayName(l);
      return name === cur;
    });
    // Keep a named orphan assignment visible; skip blank/deleted former leads
    if (!inList) {
      return [...leads, { id: '__current-assignment__', full_name: cur, username: undefined }];
    }
    return leads;
  }, [adminUsers, formData.team_lead]);

  const filteredTeamLeadOptions = useMemo(() => {
    const term = teamLeadSearch.trim().toLowerCase();
    if (!term) return editModalTeamLeadOptions;
    return editModalTeamLeadOptions.filter((lead) =>
      leadDisplayName(lead).toLowerCase().includes(term)
    );
  }, [editModalTeamLeadOptions, teamLeadSearch]);

  const editModalProjectOptions = useMemo(() => {
    const projects = resourceData?.projects || [];
    return [...projects]
      .filter((p) => p?.id != null)
      .sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
      );
  }, [resourceData?.projects]);

  const filteredFutureProjectOptions = useMemo(() => {
    const term = futureProjectsSearch.trim().toLowerCase();
    if (!term) return editModalProjectOptions;
    return editModalProjectOptions.filter((project) =>
      String(project.name || '').toLowerCase().includes(term)
    );
  }, [editModalProjectOptions, futureProjectsSearch]);

  const selectedFutureProjectIds = Array.isArray(formData.future_team) ? formData.future_team : [];

  const futureProjectsDisplayText = useMemo(() => {
    if (selectedFutureProjectIds.length === 0) return 'None';
    const names = selectedFutureProjectIds
      .map((id) => {
        const match = editModalProjectOptions.find((p) => String(p.id) === String(id));
        return match?.name || null;
      })
      .filter(Boolean);
    if (names.length === 0) return `${selectedFutureProjectIds.length} selected`;
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  }, [selectedFutureProjectIds, editModalProjectOptions]);

  const toggleFutureProject = (projectId) => {
    const id = String(projectId);
    setFormData((prev) => {
      const current = Array.isArray(prev.future_team) ? prev.future_team.map(String) : [];
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, future_team: next };
    });
  };

  useEffect(() => {
    if (!teamLeadDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (teamLeadDropdownRef.current && !teamLeadDropdownRef.current.contains(event.target)) {
        setTeamLeadDropdownOpen(false);
        setTeamLeadSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [teamLeadDropdownOpen]);

  useEffect(() => {
    if (teamLeadDropdownOpen) {
      setTeamLeadSearch('');
      setFutureProjectsDropdownOpen(false);
      setFutureProjectsSearch('');
      setTimeout(() => teamLeadSearchRef.current?.focus(), 0);
    }
  }, [teamLeadDropdownOpen]);

  useEffect(() => {
    if (!futureProjectsDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        futureProjectsDropdownRef.current &&
        !futureProjectsDropdownRef.current.contains(event.target)
      ) {
        setFutureProjectsDropdownOpen(false);
        setFutureProjectsSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [futureProjectsDropdownOpen]);

  useEffect(() => {
    if (futureProjectsDropdownOpen) {
      setFutureProjectsSearch('');
      setTeamLeadDropdownOpen(false);
      setTeamLeadSearch('');
      setTimeout(() => futureProjectsSearchRef.current?.focus(), 0);
    }
  }, [futureProjectsDropdownOpen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="resource-utilization" data-testid="resource-utilization-page">
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="text-2xl font-bold text-secondary-900">
            Resource Utilization
          </h1>
          <div className="flex items-center gap-4">
            <div className="relative">
              <button
                className={`border rounded-lg px-4 py-2 flex items-center gap-2 transition ${
                  showFilters
                    ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/40 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/60'
                    : 'bg-secondary-50 border-secondary-200 hover:bg-secondary-100 dark:bg-secondary-800 dark:border-secondary-700 dark:text-secondary-200 dark:hover:bg-secondary-700'
                }`}
                onClick={() => setShowFilters((prev) => !prev)}
                aria-expanded={showFilters}
                aria-controls="resource-utilization-filters"
                title={showFilters ? 'Hide filters' : 'Show filters'}
              >
                <FilterIcon className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-primary-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                    {activeFilterCount}
                  </span>
                )}
                {showFilters ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
            </div>

            {/* Columns visibility (People table only) */}
            {activeView === 'people' && (
            <div className="relative" ref={columnsDropdownRef}>
              <button
                onClick={() => setShowColumnsDropdown(!showColumnsDropdown)}
                className="border rounded-lg px-4 py-2 flex items-center gap-2 bg-secondary-50 hover:bg-secondary-100 transition"
                title="Toggle columns"
              >
                <Columns className="h-4 w-4" />
                Columns
                <ChevronDown className={`h-3 w-3 transition-transform ${showColumnsDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showColumnsDropdown && (
                <div className="absolute right-0 top-full z-50 bg-white border border-secondary-200 rounded-lg shadow-lg mt-2 min-w-[200px] py-2">
                  <div className="px-4 py-2 border-b border-secondary-100">
                    <span className="text-sm font-medium text-secondary-700">Show columns</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {PEOPLE_COLUMNS.map(({ key, label, locked }) => (
                      <label
                        key={key}
                        className={`flex items-center gap-2 px-4 py-2 hover:bg-secondary-50 ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={locked || !!visibleColumns[key]}
                          disabled={locked}
                          onChange={() => toggleColumn(key)}
                          data-testid={`column-toggle-${key}`}
                          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Export Button */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                disabled={exportLoading || filteredUsers.length === 0}
                className="border rounded-lg px-4 py-2 flex items-center gap-2 bg-primary-50 hover:bg-primary-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export data"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Export
                    <ChevronDown className="h-3 w-3" />
                  </>
                )}
              </button>

              {/* Export Dropdown */}
              {showExportDropdown && !exportLoading && (
                <div className="absolute right-0 top-full z-50 bg-white border rounded-lg shadow-lg mt-2 min-w-[160px]">
                  <div className="py-2">
                    <button
                      onClick={exportToCSV}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export as Excel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                placeholder="Search across all columns..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
              />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-secondary-400 hover:text-secondary-600"
                title="Clear search"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
                         {(searchTerm || selectedResourceName || 
                           (selectedDepartments.length > 0 && selectedDepartments.length < getAllDepartments().length) ||
                           (selectedCurrentProjects.length > 0 && selectedCurrentProjects.length < getAllCurrentProjects().length) ||
                           (selectedClients.length > 0 && selectedClients.length < getAllClients().length) ||
                           (selectedTeamLeads.length > 0 && selectedTeamLeads.length < getAllTeamLeads().length) ||
                           selectedHasTasks !== 'all' || selectedComments) && (
               <button
                 onClick={() => {
                   setSearchTerm('');
                   setSelectedResourceName('');
                   setSelectedDepartments([]);
                   setSelectedCurrentProjects([]);
                   setSelectedClients([]);
                   setSelectedTeamLeads([]);
                   setSelectedHasTasks('all');
                   setSelectedComments('');
                   setSelectedCohorts([]);
                   setSelectedBillable('all');
                   setSelectedKind('all');
                   setSelectedRange('this_week');
                   setShowConflictsOnly(false);
                   setCurrentPage(1);
                 }}
                 className="btn-secondary text-sm"
                 title="Clear all filters and search"
               >
                 Clear All
               </button>
             )}
          </div>
        </div>

                {/* Filter Section */}
        {showFilters && (
          <div id="resource-utilization-filters" className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Filters</h3>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="inline-flex items-center gap-1 text-sm text-secondary-600 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-secondary-200 transition"
                title="Hide filters"
              >
                Hide
                <ChevronUp className="h-4 w-4" />
              </button>
            </div>
                       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Resource Name
              </label>
              <input
                type="text"
                className="input-field w-full"
                placeholder="Filter by resource name..."
                value={selectedResourceName}
                onChange={(e) => setSelectedResourceName(e.target.value)}
              />
            </div>
            
            <MultiSelectDropdown
              label="Department"
              options={getFilteredDepartments()}
              selectedItems={selectedDepartments}
              onToggle={(dept) => toggleArrayItem(selectedDepartments, setSelectedDepartments, dept)}
              onClear={() => clearArrayFilter(setSelectedDepartments)}
              placeholder="All Departments"
            />
            
            <MultiSelectDropdown
              label="Current Projects"
              options={getFilteredCurrentProjects()}
              selectedItems={selectedCurrentProjects}
              onToggle={(project) => toggleArrayItem(selectedCurrentProjects, setSelectedCurrentProjects, project)}
              onClear={() => clearArrayFilter(setSelectedCurrentProjects)}
              placeholder="All Current Projects"
            />
             
            <MultiSelectDropdown
              label="Client"
              options={getFilteredClients()}
              selectedItems={selectedClients}
              onToggle={(client) => toggleArrayItem(selectedClients, setSelectedClients, client)}
              onClear={() => clearArrayFilter(setSelectedClients)}
              placeholder="All Clients"
            />
             
            <MultiSelectDropdown
              label="Team Lead"
              options={getFilteredTeamLeads()}
              selectedItems={selectedTeamLeads}
              onToggle={(teamLead) => toggleArrayItem(selectedTeamLeads, setSelectedTeamLeads, teamLead)}
              onClear={() => clearArrayFilter(setSelectedTeamLeads)}
              placeholder="All Team Leads"
            />
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Has Tasks
              </label>
              <select
                className="input-field w-full"
                value={selectedHasTasks}
                onChange={(e) => setSelectedHasTasks(e.target.value)}
              >
                <option value="all">All Resources</option>
                <option value="yes">With Tasks</option>
                <option value="no">Without Tasks</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Comments
              </label>
              <input
                type="text"
                className="input-field w-full"
                placeholder="Filter by comments..."
                value={selectedComments}
                onChange={(e) => setSelectedComments(e.target.value)}
              />
            </div>

            <MultiSelectDropdown
              label="Cohort"
              options={getAllCohorts()}
              selectedItems={selectedCohorts}
              onToggle={(c) => toggleArrayItem(selectedCohorts, setSelectedCohorts, c)}
              onClear={() => clearArrayFilter(setSelectedCohorts)}
              placeholder="All Cohorts"
            />

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Billability
              </label>
              <select
                className="input-field w-full"
                value={selectedBillable}
                onChange={(e) => setSelectedBillable(e.target.value)}
                data-testid="resource-filter-billable"
              >
                <option value="all">All</option>
                <option value="billable">Billable only</option>
                <option value="non_billable">Non-billable only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Type
              </label>
              <select
                className="input-field w-full"
                value={selectedKind}
                onChange={(e) => setSelectedKind(e.target.value)}
                data-testid="resource-filter-kind"
              >
                <option value="all">All (projects & products)</option>
                <option value="project">Projects only</option>
                <option value="product">Products only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1 flex items-center gap-2">
                <span>Date range</span>
                {rangeRefreshing && (
                  <span
                    className="inline-block h-3 w-3 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"
                    data-testid="resource-range-refreshing"
                    aria-label="Updating"
                  />
                )}
              </label>
              <select
                className="input-field w-full disabled:opacity-70 disabled:cursor-wait"
                value={selectedRange}
                onChange={(e) => setSelectedRange(e.target.value)}
                data-testid="resource-filter-range"
                disabled={rangeRefreshing}
                title="Scopes per-project hours, billable split, and the utilization headline (weekends, holidays, and approved leave are excluded from the target)."
              >
                {((resourceData?.filter_options?.ranges) || [
                  { value: 'this_week', label: 'This week' },
                  { value: 'last_week', label: 'Last week' },
                  { value: 'this_month', label: 'This month' },
                  { value: 'last_month', label: 'Last month' },
                  { value: 'last_3_months', label: 'Last 3 months' },
                  { value: 'last_6_months', label: 'Last 6 months' },
                ]).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Conflicts
              </label>
              <label
                className="flex items-center gap-2 h-9 px-3 border border-secondary-200 dark:border-secondary-700 rounded-md bg-white dark:bg-secondary-800 cursor-pointer text-sm text-secondary-700 dark:text-secondary-200"
                data-testid="resource-filter-conflicts"
              >
                <input
                  type="checkbox"
                  checked={showConflictsOnly}
                  onChange={(e) => setShowConflictsOnly(e.target.checked)}
                  className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                Shared / over-allocated only
              </label>
            </div>
          </div>
          
          {/* Active Filters Display */}
           {activeFilterCount > 0 && (
             <div className="mt-4 flex justify-between items-center">
               <div className="text-sm text-secondary-600">
                 <span className="inline-flex items-center gap-2">
                   <span className="font-medium">Active Filters:</span>
                   {selectedResourceName && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Name: {selectedResourceName}
                     </span>
                   )}
                   {selectedDepartments.length > 0 && selectedDepartments.length < getAllDepartments().length && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Departments: {selectedDepartments.join(', ')}
                     </span>
                   )}
                   {selectedCurrentProjects.length > 0 && selectedCurrentProjects.length < getAllCurrentProjects().length && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Projects: {selectedCurrentProjects.join(', ')}
                     </span>
                   )}
                   {selectedClients.length > 0 && selectedClients.length < getAllClients().length && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Clients: {selectedClients.join(', ')}
                     </span>
                   )}
                   {selectedTeamLeads.length > 0 && selectedTeamLeads.length < getAllTeamLeads().length && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Team Leads: {selectedTeamLeads.join(', ')}
                     </span>
                   )}
                   {selectedHasTasks !== 'all' && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Tasks: {selectedHasTasks === 'yes' ? 'With Tasks' : 'Without Tasks'}
                     </span>
                   )}
                   {selectedComments && (
                     <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded text-xs">
                       Comments: {selectedComments}
                     </span>
                   )}
                 </span>
               </div>
               <button
                 onClick={() => {
                   setSelectedResourceName('');
                   setSelectedDepartments([]);
                   setSelectedCurrentProjects([]);
                   setSelectedClients([]);
                   setSelectedTeamLeads([]);
                   setSelectedHasTasks('all');
                   setSelectedComments('');
                   setSelectedCohorts([]);
                   setSelectedBillable('all');
                   setSelectedKind('all');
                   setSelectedRange('this_week');
                   setShowConflictsOnly(false);
                 }}
                 className="btn-secondary text-sm"
               >
                 Clear Filters
               </button>
             </div>
           )}
          </div>
        )}


        {/* Results summary */}
        <div className="mt-4 text-sm text-secondary-600">
          Showing {filteredUsers.length} of {resourceData?.summary?.total_resources ?? resourceData?.users?.length ?? 0} resources
        </div>

         {/* View tabs: People / Projects / Timeline / Insights. SummaryStats
             + team-lead breakdown live on the Insights sub-view so the main
             People / Projects / Timeline surfaces stay focused on the list. */}
         <div data-testid="resource-view-tabs">
           <ViewTabs
             tabs={[
               { id: 'people', label: 'People', icon: Users },
               { id: 'projects', label: 'Projects', icon: Briefcase },
               { id: 'timeline', label: 'Timeline', icon: CalendarRange },
               { id: 'insights', label: 'Insights', icon: PieChart },
             ]}
             activeId={activeView}
             onChange={setActiveView}
           />
         </div>

         {/* Team-lead breakdown now lives on the Insights sub-view. */}

        

        {/* People view - themed list using ui/ primitives */}
        {activeView === 'people' && (
          <PeopleView
            users={filteredUsers}
            rangeLabel={resourceData?.range?.label}
            visibleColumns={visibleColumns}
            onEditUser={handleEdit}
            onManageAllocations={(user) => {
              setAllocationDrawerUser(user);
              setAllocationDrawerProjectId(null);
              setShowAllocationDrawer(true);
            }}
          />
        )}

        {/* Legacy detailed table (kept behind a flag; superseded by PeopleView) */}
        {activeView === '__legacy_people_table' && (
        <div className="card" data-testid="resource-people-table-card">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">No resources found</h3>
              <p className="text-secondary-600 mb-4">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-secondary-100 -mx-2">
              {Array.isArray(paginatedUsers) && paginatedUsers.map((user) => (
                <div key={user.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-secondary-900 truncate">
                        {typeof user.full_name === 'string' ? user.full_name : (typeof user.username === 'string' ? user.username : 'Unknown')}
                      </p>
                      <p className="text-xs text-secondary-500">{typeof user.department === 'string' ? user.department : '—'}</p>
                    </div>
                    {visibleColumns.actions && (
                      <button
                        type="button"
                        onClick={() => handleEdit(user)}
                        className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-secondary-400 hover:text-primary-600"
                        aria-label="Edit resource"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {user.current_projects?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {user.current_projects.map((p) => (
                        <a
                          key={p.id}
                          href={`/projects/${p.id}`}
                          className="bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full text-xs font-medium"
                        >
                          {typeof p.name === 'string' ? p.name : 'Project'}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-secondary-500">
                    {user.team_lead && <span>Lead: {user.team_lead}</span>}
                    {typeof user.task_count === 'number' && (
                      <button type="button" className="text-primary-600" onClick={() => handleShowTasks(user)}>
                        {user.task_count} tasks
                      </button>
                    )}
                    {visibleColumns.utilization && user.utilization && (
                      <button
                        type="button"
                        className={`font-semibold underline ${utilizationStatusClasses(user.utilization.status)}`}
                        onClick={() => openUtilizationModal(user)}
                        title="View utilization details"
                      >
                        {user.utilization.percent ?? 0}% util
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                                 <thead>
                   <tr className="border-b border-secondary-200">
                     {visibleColumns.resourceName && <th className="text-left py-3 px-4 font-medium text-secondary-700">Resource Name</th>}
                     {visibleColumns.department && <th className="text-left py-3 px-4 font-medium text-secondary-700">Department</th>}
                     {visibleColumns.currentProjects && <th className="text-left py-3 px-4 font-medium text-secondary-700">Current Projects</th>}
                     {visibleColumns.primaryProject && <th className="text-left py-3 px-4 font-medium text-secondary-700">Primary Project</th>}
                     {visibleColumns.client && <th className="text-left py-3 px-4 font-medium text-secondary-700">Client</th>}
                     {visibleColumns.futureProjects && <th className="text-left py-3 px-4 font-medium text-secondary-700">Future Projects</th>}
                     {visibleColumns.tasksAssigned && <th className="text-left py-3 px-4 font-medium text-secondary-700">Tasks Assigned</th>}
                     {visibleColumns.utilization && <th className="text-left py-3 px-4 font-medium text-secondary-700">Utilization</th>}
                     {visibleColumns.teamLead && <th className="text-left py-3 px-4 font-medium text-secondary-700">Team Lead</th>}
                     {visibleColumns.comments && <th className="text-left py-3 px-4 font-medium text-secondary-700">Comments</th>}
                     {visibleColumns.actions && <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>}
                   </tr>
                 </thead>
                <tbody>
                  {Array.isArray(paginatedUsers) && paginatedUsers.map(user => (
                    <tr key={user.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                      {visibleColumns.resourceName && (
                        <td className="py-3 px-4 text-sm font-medium">
                          {typeof user.full_name === 'string' ? user.full_name : (typeof user.username === 'string' ? user.username : 'Unknown')}
                        </td>
                      )}
                      {visibleColumns.department && (
                        <td className="py-3 px-4 text-sm">
                          {typeof user.department === 'string' ? user.department : '-'}
                        </td>
                      )}
                      {visibleColumns.currentProjects && (
                                                                     <td className="py-3 px-4 text-sm">
                          {user.current_projects && Array.isArray(user.current_projects) && user.current_projects.length > 0 ? (
                            <div className="space-y-1">
                              {user.current_projects.map(p => (
                                <div key={p.id} className="text-xs">
                                  <a
                                    href={`/projects/${p.id}`}
                                    className="bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full text-xs font-medium hover:bg-primary-100 hover:text-primary-800 transition-colors cursor-pointer inline-block"
                                    title={`Click to view ${p.name}`}
                                  >
                                    {typeof p.name === 'string' ? p.name : 'Unknown Project'}
                                  </a>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-secondary-400 text-xs italic">None</span>
                          )}
                        </td>
                      )}
                        {visibleColumns.primaryProject && (
                        <td className="py-3 px-4 text-sm">
                          {user.current_projects && Array.isArray(user.current_projects) && user.current_projects.length > 0 ? (
                            (() => {
                              const primaryId = user.primary_project_id;
                              const primaryProject = primaryId && user.current_projects.find(p => String(p.id) === String(primaryId));
                              return primaryProject ? (
                                <a
                                  href={`/projects/${primaryProject.id}`}
                                  className="text-primary-700 hover:text-primary-900 text-xs font-medium"
                                  title={`Primary: ${primaryProject.name}`}
                                >
                                  {typeof primaryProject.name === 'string' ? primaryProject.name : 'Unknown'}
                                </a>
                              ) : (
                                <span className="text-secondary-400 text-xs italic">—</span>
                              );
                            })()
                          ) : (
                            <span className="text-secondary-400 text-xs italic">—</span>
                          )}
                        </td>
                        )}
                                               {visibleColumns.client && (
                                               <td className="py-3 px-4 text-sm">
                          {user.current_projects && Array.isArray(user.current_projects) && user.current_projects.length > 0 ? (
                            <div className="space-y-1">
                              {user.current_projects.map(p => (
                                <div key={p.id} className="text-xs">
                                  {p.client ? (
                                    <span className="text-blue-600 font-medium">
                                      {p.client}
                                    </span>
                                  ) : (
                                    <span className="text-secondary-400 text-xs italic">
                                      None
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-secondary-400 text-xs italic">None</span>
                          )}
                        </td>
                        )}
                                               {visibleColumns.futureProjects && (
                                               <td className="py-3 px-4 text-sm">
                          {user.future_projects && Array.isArray(user.future_projects) && user.future_projects.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.future_projects.map(p => (
                                <button
                                  key={p.id}
                                  onClick={() => navigate(`/projects/${p.id}`)}
                                  className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium hover:bg-green-100 hover:text-green-800 transition-colors cursor-pointer"
                                  title={`Click to view ${p.name}`}
                                >
                                  {typeof p.name === 'string' ? p.name : 'Unknown Project'}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="text-secondary-400 text-xs italic">None</span>
                          )}
                        </td>
                        )}
                                                                     {visibleColumns.tasksAssigned && (
                                                                     <td className="py-3 px-4 text-sm">
                          <button
                            className="text-primary-700 underline hover:text-primary-900 font-semibold"
                            onClick={() => handleShowTasks(user)}
                          >
                            {user.task_count || 0}
                          </button>
                        </td>
                      )}
                      {visibleColumns.utilization && (
                        <td className="py-3 px-4 text-sm">
                          {user.utilization ? (
                            <button
                              type="button"
                              className={`underline font-semibold hover:opacity-80 ${utilizationStatusClasses(user.utilization.status)}`}
                              onClick={() => openUtilizationModal(user)}
                              title="View utilization details"
                            >
                              {user.utilization.percent ?? 0}%
                            </button>
                          ) : (
                            <span className="text-secondary-400 text-xs italic">—</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.teamLead && (
                      <td className="py-3 px-4 text-sm">
                        {typeof user.team_lead === 'string' ? user.team_lead : '-'}
                      </td>
                      )}
                                             {visibleColumns.comments && (
                                             <td className="py-3 px-4 text-sm">
                         {typeof user.comments === 'string' && user.comments.trim() !== '' ? user.comments : (
                           <span className="text-secondary-400 text-xs italic">None</span>
                         )}
                       </td>
                      )}
                      {visibleColumns.actions && (
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="p-1 text-secondary-400 hover:text-primary-600 transition-colors"
                            title="Edit Resource"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          
          {/* Pagination and Page Size */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
            {/* Page size selector - always visible */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-secondary-600">Show:</span>
              <select
                className="input-field w-20 text-sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-secondary-600">per page</span>
            </div>

            {/* Pagination controls - only show when multiple pages */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button 
                  className="btn-secondary px-3" 
                  disabled={currentPage === 1} 
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={`px-3 py-1 rounded transition-colors ${
                      currentPage === i + 1 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    }`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  className="btn-secondary px-3" 
                  disabled={currentPage === totalPages} 
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
            
            {/* Page info */}
            <div className="text-sm text-secondary-600">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        </div>
        )}

        {activeView === 'projects' && (
          <ProjectsView
            users={filteredUsers}
            projects={resourceData?.projects || []}
            onAddAllocation={(group) => {
              const firstMember = group.members[0]?.user;
              if (firstMember) {
                setAllocationDrawerUser(firstMember);
                setAllocationDrawerProjectId(group.project_id);
                setShowAllocationDrawer(true);
              }
            }}
          />
        )}

        {activeView === 'timeline' && (
          <TimelineView users={filteredUsers} />
        )}

        {activeView === 'insights' && (
          <InsightsView
            users={filteredUsers}
            teamLeadBreakdown={teamLeadBreakdown}
            onOpenTeamLead={openTeamLeadModal}
            rangeLabel={resourceData?.range?.label}
            teamLeadStorageKey={TEAM_LEAD_BREAKDOWN_KEY}
          />
        )}
      </div>

      {/* Allocation Drawer (per-user) */}
      <AllocationDrawer
        open={showAllocationDrawer}
        onClose={() => {
          setShowAllocationDrawer(false);
          setAllocationDrawerUser(null);
          setAllocationDrawerProjectId(null);
        }}
        user={allocationDrawerUser}
        projects={resourceData?.projects || []}
        allocations={allocationDrawerUser?.allocations || []}
        initialProjectId={allocationDrawerProjectId}
        onSaved={async () => {
          // Allocations touch every range view, so flush the entire cache
          // and refetch the current range silently.
          rangeCacheRef.current.clear();
          await fetchResourceData(selectedRange, {
            silent: true,
            bypassCache: true,
          });
        }}
      />

      {/* Edit Resource Drawer */}
      <FormDrawer
        open={showEditModal}
        onClose={() => {
          setEditingUser(null);
          setShowEditModal(false);
          setTeamLeadDropdownOpen(false);
          setTeamLeadSearch('');
          setFutureProjectsDropdownOpen(false);
          setFutureProjectsSearch('');
        }}
        title="Edit Resource"
        subtitle="Update this team member's department, team lead, and project assignments."
        icon={<Edit className="h-5 w-5" />}
        iconColor="primary"
        footer={(
          <>
            <button
              type="button"
              onClick={() => {
                setEditingUser(null);
                setShowEditModal(false);
                setTeamLeadDropdownOpen(false);
                setTeamLeadSearch('');
                setFutureProjectsDropdownOpen(false);
                setFutureProjectsSearch('');
              }}
              className="btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update
                </>
              )}
            </button>
          </>
        )}
      >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Department
                </label>
                <select
                  className="input-field"
                  value={typeof formData.department === 'string' ? formData.department : ''}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                >
                  <option value="">None</option>
                  {editModalDepartmentOptions.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative" ref={futureProjectsDropdownRef}>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Future Projects{' '}
                  <span className="text-xs text-secondary-500">
                    ({editModalProjectOptions.length} available)
                  </span>
                </label>
                <button
                  type="button"
                  className="input-field w-full text-left flex items-center justify-between gap-2"
                  onClick={() => setFutureProjectsDropdownOpen((open) => !open)}
                  aria-expanded={futureProjectsDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span
                    className={
                      selectedFutureProjectIds.length > 0
                        ? 'text-secondary-900 truncate'
                        : 'text-secondary-400'
                    }
                  >
                    {futureProjectsDisplayText}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${
                      futureProjectsDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {futureProjectsDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 shrink-0">
                      <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400" />
                        <input
                          ref={futureProjectsSearchRef}
                          type="text"
                          placeholder="Search projects..."
                          value={futureProjectsSearch}
                          onChange={(e) => setFutureProjectsSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="input-field w-full !pl-8 py-1.5 text-sm"
                        />
                        {futureProjectsSearch && (
                          <button
                            type="button"
                            onClick={() => setFutureProjectsSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      {selectedFutureProjectIds.length > 0 && (
                        <div className="flex justify-end mt-2">
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, future_team: [] }))}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Clear all
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="overflow-y-auto flex-1 min-h-0" role="listbox" aria-multiselectable="true">
                      {filteredFutureProjectOptions.map((project) => {
                        const projectId = String(project.id);
                        const projectName =
                          typeof project.name === 'string' ? project.name : 'Unknown Project';
                        const selected = selectedFutureProjectIds.map(String).includes(projectId);
                        return (
                          <button
                            key={projectId}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => toggleFutureProject(projectId)}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                              selected
                                ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/30 dark:text-primary-300'
                                : 'text-secondary-700 hover:bg-secondary-50 dark:text-secondary-200 dark:hover:bg-secondary-700/50'
                            }`}
                          >
                            <span
                              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                                selected
                                  ? 'border-primary-500 bg-primary-500 text-white'
                                  : 'border-secondary-300 dark:border-secondary-500'
                              }`}
                              aria-hidden="true"
                            >
                              {selected ? (
                                <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                                  <path
                                    d="M2.5 6.2L4.8 8.5L9.5 3.5"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : null}
                            </span>
                            <span className="truncate">{projectName}</span>
                          </button>
                        );
                      })}
                      {filteredFutureProjectOptions.length === 0 && (
                        <div className="px-4 py-3 text-sm text-secondary-500">
                          No projects match &quot;{futureProjectsSearch}&quot;
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={teamLeadDropdownRef}>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Team Lead{' '}
                  <span className="text-xs text-secondary-500">
                    ({editModalTeamLeadOptions.length} available)
                  </span>
                </label>
                <button
                  type="button"
                  className="input-field w-full text-left flex items-center justify-between gap-2"
                  onClick={() => setTeamLeadDropdownOpen((open) => !open)}
                  aria-expanded={teamLeadDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span
                    className={
                      formData.team_lead?.trim()
                        ? 'text-secondary-900 truncate'
                        : 'text-secondary-400'
                    }
                  >
                    {formData.team_lead?.trim() || 'None'}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${
                      teamLeadDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {teamLeadDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 shrink-0">
                      <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400" />
                        <input
                          ref={teamLeadSearchRef}
                          type="text"
                          placeholder="Search team leads..."
                          value={teamLeadSearch}
                          onChange={(e) => setTeamLeadSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="input-field w-full !pl-8 py-1.5 text-sm"
                        />
                        {teamLeadSearch && (
                          <button
                            type="button"
                            onClick={() => setTeamLeadSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="overflow-y-auto flex-1 min-h-0" role="listbox">
                      <button
                        type="button"
                        role="option"
                        aria-selected={!formData.team_lead?.trim()}
                        onClick={() => {
                          setFormData({ ...formData, team_lead: '' });
                          setTeamLeadDropdownOpen(false);
                          setTeamLeadSearch('');
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          !formData.team_lead?.trim()
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-secondary-700 hover:bg-secondary-50'
                        }`}
                      >
                        None
                      </button>
                      {filteredTeamLeadOptions.map((lead) => {
                        const name = leadDisplayName(lead);
                        const selected = formData.team_lead?.trim() === name;
                        return (
                          <button
                            key={lead.id || name}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              setFormData({ ...formData, team_lead: name });
                              setTeamLeadDropdownOpen(false);
                              setTeamLeadSearch('');
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                              selected
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-secondary-700 hover:bg-secondary-50'
                            }`}
                          >
                            {name}
                          </button>
                        );
                      })}
                      {filteredTeamLeadOptions.length === 0 && (
                        <div className="px-4 py-3 text-sm text-secondary-500">
                          No team leads match &quot;{teamLeadSearch}&quot;
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Primary Project
                </label>
                <select
                  className="input-field"
                  value={formData.primary_project_id || ''}
                  onChange={(e) => setFormData({...formData, primary_project_id: e.target.value})}
                >
                  <option value="">None</option>
                  {(editingUser?.current_projects || []).map(p => (
                    <option key={p.id} value={p.id}>{typeof p.name === 'string' ? p.name : 'Unknown Project'}</option>
                  ))}
                </select>
                <p className="text-xs text-secondary-500 mt-1">Select from this resource&apos;s current projects</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Cohort
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. UI/UX, Platform, Mobile..."
                  value={typeof formData.cohort === 'string' ? formData.cohort : ''}
                  onChange={(e) => setFormData({ ...formData, cohort: e.target.value })}
                  data-testid="edit-user-cohort"
                />
                <p className="text-xs text-secondary-500 mt-1">
                  A free-form grouping label used by the Resource Utilization filters.
                </p>
              </div>
              <div>
                <label
                  htmlFor="edit-user-billability"
                  className="block text-sm font-medium text-secondary-700 mb-1"
                >
                  Billability
                </label>
                <select
                  id="edit-user-billability"
                  className="input-field"
                  value={formData.billability || ''}
                  onChange={(e) => setFormData({ ...formData, billability: e.target.value })}
                  data-testid="edit-user-billability"
                >
                  {RESOURCE_BILLABILITY_OPTIONS.map((opt) => (
                    <option key={opt.value || 'inherit'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-secondary-500 mt-1">
                  Billable or Non-billable applies to all of this resource&apos;s hours and
                  overrides the project and allocation settings.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Comments
                </label>
                <textarea
                  className="input-field"
                  rows="3"
                  value={typeof formData.comments === 'string' ? formData.comments : ''}
                  onChange={(e) => setFormData({...formData, comments: e.target.value})}
                />
              </div>
            </div>
      </FormDrawer>

        {/* Utilization Reason Modal */}
        {showUtilizationModal && selectedUtilizationUser && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeUtilizationModal}
          >
            <div
              className="bg-white dark:bg-secondary-800 rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto border border-secondary-200 dark:border-secondary-700 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const util = selectedUtilizationUser.utilization || {};
                const displayName =
                  selectedUtilizationUser.full_name ||
                  selectedUtilizationUser.username ||
                  'Unknown';
                const status = util.status || 'unknown';
                return (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                          Utilization for {displayName}
                        </h3>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                          {util.week_start && util.week_end
                            ? `Week ${util.week_start} – ${util.week_end}`
                            : 'Current week (PKT)'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={closeUtilizationModal}
                        className="text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                        title="Close"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="flex items-end gap-3 mb-5">
                      <div className={`text-4xl font-bold tabular-nums ${utilizationStatusClasses(status)}`}>
                        {util.percent ?? 0}%
                      </div>
                      <span
                        className={`mb-1 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${utilizationPillClasses(status)}`}
                      >
                        {utilizationStatusLabel(status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-3">
                        <div className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                          This week
                        </div>
                        <div className="text-lg font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {(util.week_hours ?? 0)}h
                        </div>
                      </div>
                      <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-3">
                        <div className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                          Target
                        </div>
                        <div className="text-lg font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {(util.target_hours ?? 40)}h
                        </div>
                      </div>
                      <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-3">
                        <div className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                          Projects
                        </div>
                        <div className="text-lg font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {util.project_count ?? 0}
                        </div>
                      </div>
                      <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-3">
                        <div className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
                          Tasks
                        </div>
                        <div className="text-lg font-semibold text-secondary-900 dark:text-white tabular-nums">
                          {util.task_count ?? 0}
                          <span className="text-sm font-normal text-secondary-500 dark:text-secondary-400">
                            {' '}({util.active_task_count ?? 0} active)
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-secondary-700 dark:text-secondary-300 leading-relaxed">
                      {util.reason ||
                        `Tracked ${util.week_hours ?? 0}h of ${util.target_hours ?? 40}h this week (${util.percent ?? 0}%).`}
                    </p>

                    <div className="mt-6 pt-4 border-t border-secondary-200 dark:border-secondary-700 flex justify-end">
                      <button type="button" className="btn-secondary" onClick={closeUtilizationModal}>
                        Close
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Team Lead Breakdown Modal */}
        {showTeamLeadModal && selectedTeamLeadGroup && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeTeamLeadModal}
          >
            <div
              className="bg-white dark:bg-secondary-800 rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto border border-secondary-200 dark:border-secondary-700 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Resources under {selectedTeamLeadGroup.lead}
                  </h3>
                  <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                    {selectedTeamLeadGroup.count} resource{selectedTeamLeadGroup.count === 1 ? '' : 's'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeTeamLeadModal}
                  className="text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {selectedTeamLeadGroup.users.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-secondary-600 dark:text-secondary-400">No resources found for this team lead.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedTeamLeadGroup.users.map((resource) => {
                    const displayName = resource.full_name || resource.username || 'Unknown';
                    const fallback =
                      (typeof displayName === 'string' && displayName.trim().charAt(0).toUpperCase()) || 'U';
                    const canOpenProfile = Boolean(resource.id);

                    return (
                      <button
                        key={resource.id || resource.email || displayName}
                        type="button"
                        disabled={!canOpenProfile}
                        onClick={() => {
                          if (!canOpenProfile) return;
                          closeTeamLeadModal();
                          navigate(`/users/${resource.id}`);
                        }}
                        className={`w-full text-left border border-secondary-200 dark:border-secondary-700 rounded-lg px-4 py-3 transition-colors flex items-center gap-3 ${
                          canOpenProfile
                            ? 'hover:bg-primary-50 hover:border-primary-300 dark:hover:bg-primary-900/20 dark:hover:border-primary-600 cursor-pointer'
                            : 'opacity-70 cursor-default'
                        }`}
                        title={canOpenProfile ? `Open ${displayName}'s profile` : displayName}
                      >
                        <Avatar
                          blobName={resource.avatar}
                          alt={displayName}
                          size="md"
                          fallbackText={fallback}
                          className="flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-secondary-900 dark:text-white truncate">
                            {displayName}
                          </div>
                          <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                            <span>{resource.department || 'No department'}</span>
                            <span>
                              {(resource.task_count || 0)} task{(resource.task_count || 0) === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-secondary-200 dark:border-secondary-700 flex justify-end">
                <button type="button" className="btn-secondary" onClick={closeTeamLeadModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

                {/* Tasks Modal */}
         {showTasksModal && (
           <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleCloseTasksModal}>
             <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
             <div className="flex items-center justify-between mb-4">
               <div>
                 <h3 className="text-lg font-semibold">
                   Tasks for {selectedUserName}
                 </h3>
                 <p className="text-sm text-secondary-600 mt-1">
                   {selectedUserTasks.length} total tasks • Page {taskModalPage} of {taskModalTotalPages}
                 </p>
               </div>
               <button
                 onClick={() => handleCloseTasksModal()}
                 className="text-secondary-400 hover:text-secondary-600"
               >
                 <X className="h-5 w-5" />
               </button>
             </div>
             
             {tasksLoading ? (
               <div className="text-center py-8">
                 <div className="flex items-center justify-center space-x-2">
                   <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                   <p className="text-secondary-600">Loading tasks...</p>
                 </div>
               </div>
             ) : selectedUserTasks.length === 0 ? (
               <div className="text-center py-8">
                 <p className="text-secondary-600">No tasks assigned to this user.</p>
               </div>
             ) : (
               <>
                 <div className="space-y-3">
                   {paginatedTasks.map(task => (
                     <div key={task.id} className="border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors">
                       <div className="flex items-center justify-between">
                         <div className="flex-1">
                           <h4 className="font-medium text-secondary-900 mb-2">
                             {task.name || 'Untitled Task'}
                           </h4>
                           <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-secondary-600">
                             <div>
                               <span className="font-medium">Project:</span> {task.project_name || 'No Project'}
                             </div>
                             <div>
                               <span className="font-medium">Status:</span> 
                               <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                                 task.status === 'completed' ? 'bg-green-100 text-green-800' :
                                 task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                 task.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                 task.status === 'Done' ? 'bg-green-100 text-green-800' :
                                 task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                 task.status === 'To Do' ? 'bg-yellow-100 text-yellow-800' :
                                 'bg-gray-100 text-gray-800'
                               }`}>
                                 {task.status || 'Unknown'}
                               </span>
                             </div>
                             <div>
                               <span className="font-medium">Priority:</span> 
                               <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                                 task.priority_level === 'high' ? 'bg-red-100 text-red-800' :
                                 task.priority_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                 task.priority_level === 'low' ? 'bg-green-100 text-green-800' :
                                 'bg-gray-100 text-gray-800'
                               }`}>
                                 {task.priority_level || 'Not Set'}
                               </span>
                             </div>
                             <div>
                               <span className="font-medium">Your Hours:</span> 
                               <span className="ml-1 px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                 {task.tracked_hours > 0 ? `${task.tracked_hours.toFixed(2)}h` : '0h'}
                               </span>
                               {/* Debug info - remove this later */}
                               <div className="text-xs text-gray-500 mt-1">
                                 From {task.time_entries_count} time entries
                               </div>
                             </div>
                           </div>
                           {task.description && (
                             <p className="text-sm text-secondary-600 mt-2 line-clamp-2">
                               {task.description}
                             </p>
                           )}
                         </div>
                                                    <div className="flex flex-col items-end gap-2">
                           <a
                             href={`/tasks/${task.id}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm text-center inline-block"
                           >
                             View Task
                           </a>
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
                
                 {/* Task Modal Pagination */}
                 {taskModalTotalPages > 1 && (
                   <div className="flex justify-center items-center gap-2 mt-6 pt-4 border-t border-secondary-200">
                     <button 
                       className="btn-secondary px-3 py-1 text-sm" 
                       disabled={taskModalPage === 1} 
                       onClick={() => setTaskModalPage(taskModalPage - 1)}
                     >
                       Previous
                     </button>
                     <span className="text-sm text-secondary-600">
                       Page {taskModalPage} of {taskModalTotalPages}
                     </span>
                     <button 
                       className="btn-secondary px-3 py-1 text-sm" 
                       disabled={taskModalPage === taskModalTotalPages} 
                       onClick={() => setTaskModalPage(taskModalPage + 1)}
                     >
                       Next
                     </button>
                   </div>
                 )}
               </>
             )}
           </div>
         </div>
       )}
    </div>
   );
 };

export default ResourceUtilization; 